import {
	Currency,
	Dtos,
	type SuretyAccountId,
	type SuretyBondId,
	type SuretyQuoteId,
	type UserId,
} from "@inrev/common";
import { DateTime } from "luxon";
import { match } from "ts-pattern";
import { buildTrend, type MetricTrend, toMs } from "../../../shared/dashboard/metrics/buildTrend";
import { type DashboardScope, scopeMatches } from "../scope";

type ActionItem = Dtos.ActionItem.Get.Response;

type AccountPreview = Pick<
	Dtos.SuretyAccount.Preview.Get.Response,
	"hasBeenActive" | "status" | "underwriterUserId" | "createdAt"
>;
type BondPreview = Pick<
	Dtos.SuretyBond.Preview.Get.Response,
	"id" | "accountId" | "status" | "type" | "amount" | "underwriterUserId" | "effectiveDate"
>;
type BondRequestPreview = Pick<
	Dtos.SuretyQuote.Preview.Get.Response,
	"id" | "accountId" | "status" | "underwriterUserId" | "createdAt"
>;

export type DashboardMetricsInput = {
	scope: DashboardScope;
	currentUserId: UserId;
	accountPreviews: AccountPreview[];
	bondPreviews: BondPreview[];
	bondRequestPreviews: BondRequestPreview[];
	actionItems: ActionItem[];
	/** Injectable "now" for deterministic trend windows in tests. */
	nowMs?: number;
};

export type DashboardMetrics = {
	openSubmissions: number;
	openBondRequests: number;
	actionItemAccounts: number;
	openExposure: Currency;
	trends: {
		openSubmissions: MetricTrend;
		openBondRequests: MetricTrend;
		actionItemAccounts: MetricTrend;
		openExposure: MetricTrend;
	};
};

const resolveActionItemAccountId = (
	actionItem: ActionItem,
	bondIdToAccountId: Map<SuretyBondId, SuretyAccountId>,
	quoteIdToAccountId: Map<SuretyQuoteId, SuretyAccountId>,
): SuretyAccountId | undefined =>
	match(actionItem)
		.with({ relationType: "account" }, ({ relationId }) => relationId)
		.with({ relationType: "bond" }, ({ relationId }) => bondIdToAccountId.get(relationId))
		.with({ relationType: "quote" }, ({ relationId }) => quoteIdToAccountId.get(relationId))
		.exhaustive();

export const computeMetrics = ({
	scope,
	currentUserId,
	accountPreviews,
	bondPreviews,
	bondRequestPreviews,
	actionItems,
	nowMs,
}: DashboardMetricsInput): DashboardMetrics => {
	const now = nowMs ?? DateTime.now().toMillis();
	const scopeMatch = (underwriterUserId: UserId) =>
		scopeMatches(scope, underwriterUserId, currentUserId);

	const openSubmissionAccounts = accountPreviews.filter(
		(account) =>
			!account.hasBeenActive &&
			(account.status === "review" || account.status === "approved") &&
			scopeMatch(account.underwriterUserId),
	);
	const openSubmissions = openSubmissionAccounts.length;

	const openBondRequestList = bondRequestPreviews.filter(
		(request) =>
			(request.status === "accepted" ||
				request.status === "approved" ||
				request.status === "pending" ||
				request.status === "review") &&
			scopeMatch(request.underwriterUserId),
	);
	const openBondRequests = openBondRequestList.length;

	const bondIdToAccountId = new Map<SuretyBondId, SuretyAccountId>(
		bondPreviews.map((bond) => [bond.id, bond.accountId]),
	);
	const quoteIdToAccountId = new Map<SuretyQuoteId, SuretyAccountId>(
		bondRequestPreviews.map((request) => [request.id, request.accountId]),
	);

	// Track when each account first entered action-item state so the trend
	// reflects accounts (the metric), not raw action-item inflow.
	const actionItemAccountFirstSeenMs = new Map<SuretyAccountId, number>();
	for (const actionItem of actionItems) {
		if (actionItem.completedAt) continue;
		if (scope === "me" && actionItem.assigneeId !== currentUserId) continue;
		const accountId = resolveActionItemAccountId(actionItem, bondIdToAccountId, quoteIdToAccountId);
		if (!accountId) continue;
		const atMs = toMs(actionItem.createdAt);
		const existing = actionItemAccountFirstSeenMs.get(accountId);
		if (existing === undefined || atMs < existing)
			actionItemAccountFirstSeenMs.set(accountId, atMs);
	}
	const actionItemAccounts = actionItemAccountFirstSeenMs.size;

	const openExposureBonds = bondPreviews.filter(
		(bond) => bond.status === "open" && bond.type === "final" && scopeMatch(bond.underwriterUserId),
	);
	const openExposure = Currency.sum(openExposureBonds.map((bond) => bond.amount));

	return {
		openSubmissions,
		openBondRequests,
		actionItemAccounts,
		openExposure,
		trends: {
			openSubmissions: buildTrend(
				openSubmissionAccounts.map((account) => ({ atMs: toMs(account.createdAt), weight: 1 })),
				now,
			),
			openBondRequests: buildTrend(
				openBondRequestList.map((request) => ({ atMs: toMs(request.createdAt), weight: 1 })),
				now,
			),
			actionItemAccounts: buildTrend(
				[...actionItemAccountFirstSeenMs.values()].map((atMs) => ({ atMs, weight: 1 })),
				now,
			),
			openExposure: buildTrend(
				openExposureBonds.map((bond) => ({ atMs: toMs(bond.effectiveDate), weight: bond.amount })),
				now,
				{ currency: true },
			),
		},
	};
};
